/**
 * Single source of truth for what kind of card a product actually is.
 *
 * revolving = echte Kreditkarte mit Teilzahlung (Sollzins/eff. Jahreszins relevant)
 * charge    = Charge-Karte, monatlicher Vollausgleich (kein Sollzins)
 * debit     = Debitkarte, direkte Belastung des Girokontos (kein Sollzins)
 * prepaid   = Prepaid-Karte, Guthabenbasis (kein Kreditrahmen)
 */
export type CardCategory = "revolving" | "charge" | "debit" | "prepaid";

const VALID: CardCategory[] = ["revolving", "charge", "debit", "prepaid"];

/** Resolve the card category, with a safe fallback for rows not yet classified. */
export function getCardCategory(card: any): CardCategory {
  const stored = String(card?.card_category ?? "").toLowerCase();
  if (VALID.includes(stored as CardCategory)) return stored as CardCategory;

  if (card?.is_prepaid === true) return "prepaid";
  const legacy = String(card?.card_type ?? "").toLowerCase();
  if (legacy === "prepaid") return "prepaid";
  if (legacy === "debit") return "debit";
  if (legacy === "charge") return "charge";

  const name = `${card?.display_name ?? ""} ${card?.name ?? ""}`.toLowerCase();
  if (name.includes("debit")) return "debit";

  return "revolving";
}

export const CARD_CATEGORY_LABEL: Record<CardCategory, string> = {
  revolving: "Kreditkarte",
  charge: "Charge-Karte",
  debit: "Debitkarte",
  prepaid: "Prepaid-Karte",
};

export const CARD_CATEGORY_SHORT_LABEL: Record<CardCategory, string> = {
  revolving: "Kreditkarte",
  charge: "Charge-Karte",
  debit: "Debitkarte",
  prepaid: "Prepaidkarte",
};

export const CARD_CATEGORY_TOOLTIP: Record<CardCategory, string> = {
  revolving:
    "Echte Kreditkarte: Sie können den Betrag in Raten zurückzahlen – dabei fallen Sollzinsen an.",
  charge:
    "Charge-Karte: Der Umsatz wird monatlich vollständig abgebucht. Es gibt keine Teilzahlung und damit keine Sollzinsen.",
  debit:
    "Debitkarte: Der Betrag wird direkt vom Girokonto abgebucht. Es gibt keinen Kreditrahmen und keine Sollzinsen.",
  prepaid:
    "Prepaid-Karte: Sie laden die Karte mit Guthaben auf. Es gibt keinen Kreditrahmen und keine Sollzinsen.",
};

/** Only real credit cards may show APR, Sollzins, Teilzahlung or repayment examples. */
export function showsCreditMetrics(card: any): boolean {
  return getCardCategory(card) === "revolving";
}

export function isPrepaidCard(card: any): boolean {
  return getCardCategory(card) === "prepaid";
}

/** Overdraft rate of the linked current account (debit cards only). */
export function getOverdraftRate(card: any): number | null {
  const value = card?.account_overdraft_rate;
  return typeof value === "number" && value > 0 ? value : null;
}

export function formatPercentDE(value: number, digits = 2): string {
  return `${value.toFixed(digits).replace(".", ",")} %`;
}
